import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import mapboxgl from "mapbox-gl";

import classNames from "classnames";

import styles from "./MapElement.module.scss";

mapboxgl.accessToken =
  "pk.eyJ1IjoicGV0ZXJyb2RlbjkzIiwiYSI6ImNqeXl1YWd1MTFrdTEzY3BobjVxa3NoMzUifQ.au3Z5_INqkxxbFSFRh5DGw";

interface MapElementProps {
  className?: string;
  options?: mapboxgl.MapboxOptions;
  center?: mapboxgl.LngLatLike;
}

interface MapElementContextProps {
  map?: mapboxgl.Map;
}

const DEFAULT_MAX_BOUNDS: [[number, number], [number, number]] = [
  [-0.14151134, 51.47505249],
  [-0.06735362, 51.51799038],
];

const MapElementContext = createContext<MapElementContextProps>({});

const MapElement: React.FC<MapElementProps> = ({
  className,
  children,
  options = {},
  center = { lng: -0.10134794, lat: 51.49547594 },
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<mapboxgl.Map>();

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    const m = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/peterroden93/ckebfk3pw17g519nwr0lar9qs",
      center,
      zoom: 14,
      maxBounds: DEFAULT_MAX_BOUNDS,
      ...options,
    });

    m.on("load", () => {
      m.resize();
    });

    setMap(m);

    const linkEl = document.createElement("link");
    linkEl.href = "https://api.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.css";
    linkEl.rel = "stylesheet";
    document.head.appendChild(linkEl);

    return () => m.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map || !center) {
      return;
    }

    map.flyTo({ center });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center]);

  return (
    <MapElementContext.Provider value={{ map }}>
      <div ref={mapContainerRef} className={classNames(className, styles.map)}>
        {children}
      </div>
    </MapElementContext.Provider>
  );
};

const useMapElement = () => useContext(MapElementContext);

export { MapElement, useMapElement };
